import EmailVerificationContainer from "containers/EmailVerificationContainer"
import InternshipLearningContainer from "containers/InternshipLearningContainer"
import LearningHubContainer from "containers/LearningHubContainer"
import MentorshipContainer from "containers/Mentorship/MentorshipContainer"
import MoreInfoContainer from "containers/MoreInfoContainer"
import ResetPasswordContainer from "containers/ResetPasswordContainer"
import SignInContainer from "containers/SignInContainer"
import SignupContainer from "containers/SignupContainer"
import StageContainer from "containers/StageContainer"
import SurveyContainer from "containers/SurveyContainer"
import SurveyListingContainer from "containers/SurveyListingContainer"
import Dev from "Dev"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import { Route, Switch, useHistory } from "react-router-dom"
import Session from "views/session/Session"
import HomePage from 'views/Home/HomePage'
import ProtectedRoute from "./components/navigation/ProtectedRoute"
import { initChatInstance } from "chat/WebSocket";
import ChatFullScreenContainer from "containers/ChatFullScreenContainer";
import TermsAndConditions from 'views/TermsAndConditions'
import TermsAndConditionPopup from 'components/TermsAndConditionPopup'
import NCSRedirectHandler from 'views/NCS/NcsRedirectHandler'
import NcsRegister from 'views/NCS/NcsRegister'
import NcsLogin from 'views/NCS/NcsLogin'
import SsoRedirectHandler from 'views/NCS/SsoRedirectHandler'
import CalendarCallback from 'components/CalendarCallback'
import CalendarConnect from 'components/CalendarConnect'
import JoinVideoCallContainer from 'containers/JoinVideoCallContainer'
import { updateNotificationInteraction } from "./api/messaging";
import { myFirebase } from "./firebase/firebase";

function App(props) {
  const { isAuthenticated, otpVerificationStatus, isEmailVerified, isVerifying } = props;
  const history = useHistory();

  initChatInstance();

  // Helper function to track notification interaction
  const trackNotificationOpened = async (notificationId, notificationType = null) => {

    if (!notificationId) {
      console.warn("⚠️ [APP] No notificationId provided, skipping tracking");
      return;
    }

    if (!isAuthenticated) {
      console.warn("⚠️ [APP] User not authenticated, skipping tracking");
      return;
    }

    // Determine if this is a chat notification (chat or incoming_call)
    const isChatNotification = notificationType === 'chat' || notificationType === 'incoming_call';
    const endpoint = isChatNotification
      ? `/api/v1/notifications/chat/${notificationId}/`
      : `/api/v1/notifications/events/${notificationId}/`;

    try {
      const idToken = myFirebase.auth().currentUser
        ? await myFirebase.auth().currentUser.getIdToken()
        : null;

      if (idToken) {
        const auth = { idToken };
        const response = await updateNotificationInteraction(notificationId, "opened", auth, isChatNotification);

      } else {
        console.warn("⚠️ [APP] No ID token available, skipping API call");
      }
    } catch (error) {
      console.error("❌ [APP] Failed to track notification interaction:", error);
      console.error("❌ [APP] Error details:", {
        notificationId,
        notificationType,
        isChatNotification,
        error: error.message,
        stack: error.stack
      });
    }
  };

  // ✅ Check for redirect param from SW
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectFromQuery = params.get('redirect');
    const notificationIdFromQuery = params.get('notification_id');

    if (redirectFromQuery) {
      localStorage.setItem('redirectAfterLogin', redirectFromQuery);
      // Remove the query param from URL
      window.history.replaceState({}, '', '/');
    }

    // Track notification if notification_id is in URL
    const notificationTypeFromQuery = params.get('notification_type');
    if (notificationIdFromQuery) {
      if (isAuthenticated) {
        trackNotificationOpened(notificationIdFromQuery, notificationTypeFromQuery);
      } else {
        // Store notification_id and type to track after login
        localStorage.setItem('pendingNotificationId', notificationIdFromQuery);
        if (notificationTypeFromQuery) {
          localStorage.setItem('pendingNotificationType', notificationTypeFromQuery);
        }
      }
      // Remove notification_id and type from URL
      params.delete('notification_id');
      params.delete('notification_type');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // Track pending notification after authentication
    if (isAuthenticated) {
      const pendingNotificationId = localStorage.getItem('pendingNotificationId');
      const pendingNotificationType = localStorage.getItem('pendingNotificationType');
      if (pendingNotificationId) {
        trackNotificationOpened(pendingNotificationId, pendingNotificationType);
        localStorage.removeItem('pendingNotificationId');
        localStorage.removeItem('pendingNotificationType');
      }
    }

    // 2️⃣ Listen for messages from SW (optional, only works if tab is already open)
    const handleServiceWorkerMessage = async (event) => {

      if (event.data?.type === 'NOTIF_REDIRECT') {
        // Parse the full URL to preserve query parameters
        const redirectUrl = new URL(event.data.path);
        const pathname = redirectUrl.pathname;
        const searchParams = redirectUrl.searchParams;

        // Store redirect path (without query params for redirectAfterLogin)
        localStorage.setItem('redirectAfterLogin', pathname);

        // Extract channel_id from query params and store it
        const channelId = searchParams.get('channel_id');
        if (channelId) {
          localStorage.setItem('openChannelId', channelId);
        }

        // Build the navigation path with query params
        const navigationPath = searchParams.toString()
          ? `${pathname}?${searchParams.toString()}`
          : pathname;


        // Track notification interaction if notification_id is present
        if (event.data.notificationId) {
          const notifType = event.data.notificationType || searchParams.get('notification_type');
          if (isAuthenticated) {
            await trackNotificationOpened(event.data.notificationId, notifType);
          } else {
            localStorage.setItem('pendingNotificationId', event.data.notificationId);
            if (notifType) {
              localStorage.setItem('pendingNotificationType', notifType);
            }
          }
        } else {
          console.warn("⚠️ [APP] No notification_id in service worker message");
        }

        history.push(navigationPath);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    } else {
      console.warn("⚠️ [APP] Service workers not supported");
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [isAuthenticated, history]);

  return (
    <>
      <Switch>
        <Route exact path="/" component={HomePage} />
        <Route exact path="/signin" component={SignInContainer}/>
        <Route exact path="/emailverify" component={EmailVerificationContainer}/>
        <Route exact path="/calendar/callback" component={CalendarCallback} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <Route exact path="/calendar/connect/:provider" component={CalendarConnect} />
        <ProtectedRoute path="/video/join-meeting/:roomId" component={JoinVideoCallContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute exact path="/stage" component={StageContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute exact path="/survey/:id?" component={SurveyContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute path="/surveylisting" component={SurveyListingContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute exact path="/signup" component={SignupContainer} isAuthenticated={otpVerificationStatus || isEmailVerified} isVerifying={isVerifying}/>
        <ProtectedRoute path="/session/" component={Session} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute path="/learninghub/:id?" component={LearningHubContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute path="/mentorship" component={MentorshipContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute path="/internship" component={InternshipLearningContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <ProtectedRoute path="/chat" component={ChatFullScreenContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying} />
        <Route exact path="/resetpassword" component={ResetPasswordContainer}/>
        <ProtectedRoute path="/moreinfo" component={MoreInfoContainer} isAuthenticated={isAuthenticated} isVerifying={isVerifying}/>
        <Route exact path="/dev" component={Dev}/>
        <Route exact path="/privacypolicy" component={TermsAndConditions}/>
        <Route exact path="/termsofservice" component={TermsAndConditions}/>
        <Route exact path="/codeofconduct" component={TermsAndConditions}/>
        <Route exact path="/user/ncs" component={NCSRedirectHandler} />
        <Route exact path="/ncs-register" component={NcsRegister} />
        <Route exact path="/ncs-login" component={NcsLogin} />
        <Route exact path="/sso-login" component={SsoRedirectHandler} />
      </Switch>
      <TermsAndConditionPopup />
    </>
  );
}

function mapStateToProps(state) {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    otpVerificationStatus: state.auth.otpVerificationStatus,
    isEmailVerified: state.auth.isEmailVerified,
    isVerifying: state.auth.isVerifying
  };
}

export default connect(mapStateToProps)(App);
